/* eslint-disable @typescript-eslint/no-empty-object-type */

import {
	CommissionsEntry,
	Decimal,
	Maybe,
	PayoutsSummaryRow,
	SalesInfo,
	SavedRoster,
	SizeChart,
	UserError,
} from '../../common/data.js';
import { DashboardCommissionsProduct } from '../components/dashboard/commissions.js';
import { Cart, CartProductItem, DesignPriceBreakdown } from '../../common/data/cart.js';
import { isQuerySafeString, maybe, maybePipe } from '../../common/util.js';
import { reportEventsFromServer } from './events.js';
import {
	Design,
	ProductTemplate,
	ProductTemplateInventory,
	SavedDesign,
	Template,
	Upload,
} from '../../common/data/designs.js';
import { CreditLimitTotals, Payment, PaymentMethod, PaymentOption } from '../../common/data/payments.js';
import { AuthResponse, OwnUser } from '../../common/data/users.js';
import { Address, AddressInfo } from '../../common/data/address.js';
import {
	Store,
	StoreCollection,
	StoreProduct,
	StoreProductStatsRow,
	StoreUploadsService,
	Tier,
	TierCategory,
	TierCategoryGroup,
} from '../../common/data/stores.js';
import {
	ComputedProduct,
	ComputedProductInfo,
	FullPublicProduct,
	ProductCategory,
	ProductCategoryDimension,
	ProductSortType,
	PublicProduct,
	WithFeatures,
} from '../../common/data/products.js';
import { Promocode, PromocodeIncludesOptions, PromocodeWithStats } from '../../common/data/promocodes.js';
import { Shortlink, ShortlinkData } from '../../common/data/shortlinks.js';
import {
	Order,
	OrderItem,
	OrderItemFeedbackEntry,
	OrderItemPhoto,
	OrderItemStatus,
	OrderItemsTracking,
	OrderSummary,
	PrintFile,
	SalesOrder,
} from '../../common/data/orders.js';
import { PublicFlow } from '../../common/data/flows.js';
import { ProductionTimeEntry } from '../../common/data/admin.js';
import { VendorPurchaseOrder } from '../../common/data/vendors.js';
import { MemberStatsRow } from '../components/dashboard/league-members.js';
import {
	AccountAccess,
	AccountAccessData,
	AccountPermission,
	AccountWithAccess,
	OwnAccount,
	PublicAccount,
} from '../../common/data/accounts.js';
import { Context } from '../../common/context.js';
import { Message, MessageCategory, MessageDeliveryMethod, UnreadMessageCounts } from '../../common/data/messages.js';
import { getSiteContext } from '../templates/site.js';
import { ColorTuple } from '../../common/data/colors.js';
import { ApiError, ApiErrorData } from '../../common/errors.js';
import { CurrencyId } from '../../common/data/countries.js';

interface FileTuple {
	path: (string | number)[];
	blob: Blob;
}

function extractBlobs(obj: any) {

	const blobs: FileTuple[] = [];

	const rec = (obj: any, path: (string | number)[] = []): any => {

		if (!obj) return obj;

		if (Array.isArray(obj)) {
			return obj.map((item, i) => {
				const np = path.concat(i);
				if (item instanceof Blob) {
					blobs.push({ path: np, blob: item });
					return void 0;
				}
				return rec(item, np);
			});
		}

		if (typeof obj == 'object') {
			const clone: any = {};
			for (const key in obj) {
				const np = path.concat(key);
				if (obj[key] instanceof Blob) {
					blobs.push({ path: np, blob: obj[key] });
				}
				else {
					clone[key] = rec(obj[key], np);
				}
			}
			return clone;
		}

		return obj;

	};

	const json = rec(obj);

	return { json, blobs };

}

function xhrToPromise(xhr: XMLHttpRequest) {
	return new Promise((resolve, reject) => {
		xhr.onerror = e => reject(new ApiError({
			error: 'serviceUnavailable',
			message: 'Connection error',
		}));
		xhr.onload = () => {
			try {

				const res = JSON.parse(xhr.responseText);

				if (res.error) reject(new ApiError(res.error));
				else resolve(res.result);

				if (res.events?.length) setTimeout(() => reportEventsFromServer(res.events), 500);

			}
			catch (e) {
				reject(new ApiError({
					error: 'serviceUnavailable',
					message: 'Invalid response from server',
				}));
			}
		};
	});
}

function setHeaders(xhr: XMLHttpRequest) {
	const sc = getSiteContext();
	const ctx = (sc?.context.peek() ?? (window as any).siteContext?.context) as Maybe<Context>;
	maybe(ctx?.cookies.csrf, csrf => xhr.setRequestHeader('x-csrf-token', csrf));
	maybe(sc?.account.peek() ?? ctx?.account, a => xhr.setRequestHeader('x-active-account', a.id));
	maybe(sc?.cart.peek(), c => xhr.setRequestHeader('x-active-cart', c.id));
}

export function postToAPI(fun: string, params?: Maybe<{ [key: string]: any }>) {

	const xhr = new XMLHttpRequest();
	xhr.open('POST', '/api/' + fun);
	setHeaders(xhr);
	
	const { json, blobs } = extractBlobs(params);
	if (blobs.length) {
		const fd = new FormData();
		fd.append('json', JSON.stringify(json));
		for (const bd of blobs) fd.append(btoa(JSON.stringify(bd.path)), bd.blob);
		xhr.send(fd);
	}
	else {
		xhr.send(JSON.stringify(json));
	}

	return xhrToPromise(xhr);

}

function stringifyParam(v: any) {
	return typeof v == 'string' && isQuerySafeString(v) ? v : JSON.stringify(v ?? null);
}

export async function getFromAPI(fun: string, params?: Maybe<{ [key: string]: any }>) {

	const qs = maybePipe(
		params,
		Object.entries,
		p => p.map(([k, v]) => [k, stringifyParam(v)]),
		p => new URLSearchParams(p).toString(),
	);

	const xhr = new XMLHttpRequest();
	xhr.open('GET', `/api/${fun}${qs ? `?${qs}` : ''}`);
	setHeaders(xhr);
	xhr.send();

	return xhrToPromise(xhr);

}

type File = Blob;

// DEFINITIONS -- autogenerated below this line, to refresh run ./script.sh generateAPI

export type AcceptAccountInviteParams = { message: number; };
export type AcceptAccountInviteResult = { success: boolean; account: AccountWithAccess; };
export const acceptAccountInvite = (params: AcceptAccountInviteParams) => postToAPI('acceptAccountInvite', params) as Promise<AcceptAccountInviteResult>;

export type ActivateCartParams = { id: string; };
export type ActivateCartResult = { cart: Cart; };
export const activateCart = (params: ActivateCartParams) => postToAPI('activateCart', params) as Promise<ActivateCartResult>;

export type AddAccountAccessParams = { email: string; permissions: AccountPermission[]; };
export type AddAccountAccessResult = { accountAccess: AccountAccess; };
export const addAccountAccess = (params: AddAccountAccessParams) => postToAPI('addAccountAccess', params) as Promise<AddAccountAccessResult>;

export type AddAccountAddressParams = Maybe<{ name_prefix?: Maybe<string>; first_name?: Maybe<string>; middle_name?: Maybe<string>; last_name?: Maybe<string>; name_suffix?: Maybe<string>; company?: Maybe<string>; email?: Maybe<string>; phone?: Maybe<string>; address?: Maybe<string>; address_2?: Maybe<string>; city?: Maybe<string>; region?: Maybe<string>; state?: Maybe<string>; country?: Maybe<string>; postcode?: Maybe<string>; }> | undefined;
export type AddAccountAddressResult = { success?: Maybe<boolean>; errors?: Maybe<UserError[]>; };
export const addAccountAddress = (params?: AddAccountAddressParams) => postToAPI('addAccountAddress', params) as Promise<AddAccountAddressResult>;

export type AddDesignPackageToCartParams = { product: string; uploads?: Maybe<string[]>; products?: Maybe<string[]>; kits?: Maybe<string[]>; templates?: Maybe<string[]>; colorList?: Maybe<Maybe<number[]>[]>; stockColorList?: Maybe<Maybe<number[]>[]>; comment?: Maybe<string>; };
export type AddDesignPackageToCartResult = { id: string; };
export const addDesignPackageToCart = (params: AddDesignPackageToCartParams) => postToAPI('addDesignPackageToCart', params) as Promise<AddDesignPackageToCartResult>;

export type AddDesignToCartParams = { design: string; roster: unknown[]; };
export type AddDesignToCartResult = { id: string; };
export const addDesignToCart = (params: AddDesignToCartParams) => postToAPI('addDesignToCart', params) as Promise<AddDesignToCartResult>;

export type AddDesignToStoreParams = { store: string; savedDesign: string; name: string; description?: Maybe<string>; price: number; options?: Maybe<{ allowSwitchingTemplate?: boolean; allowFlag?: boolean; allowName?: boolean; allowNumber?: boolean; allowAlias?: boolean; }>; visible: boolean; collection?: Maybe<string>; price_locked?: Maybe<boolean>; };
export type AddDesignToStoreResult = { design: string; };
export const addDesignToStore = (params: AddDesignToStoreParams) => postToAPI('addDesignToStore', params) as Promise<AddDesignToStoreResult>;

export type AddGiftcardToCartParams = { amount: number; };
export type AddGiftcardToCartResult = { id: string; };
export const addGiftcardToCart = (params: AddGiftcardToCartParams) => postToAPI('addGiftcardToCart', params) as Promise<AddGiftcardToCartResult>;

export type AddOrderItemToCartParams = { item: number; };
export type AddOrderItemToCartResult = { id: string; };
export const addOrderItemToCart = (params: AddOrderItemToCartParams) => postToAPI('addOrderItemToCart', params) as Promise<AddOrderItemToCartResult>;

export type AddSavedDesignToCartParams = { design: string; };
export type AddSavedDesignToCartResult = { id: string; };
export const addSavedDesignToCart = (params: AddSavedDesignToCartParams) => postToAPI('addSavedDesignToCart', params) as Promise<AddSavedDesignToCartResult>;

export type AddStoreCollectionParams = { store: string; name?: Maybe<string>; passphrase?: Maybe<string>; };
export type AddStoreCollectionResult = { success: boolean; };
export const addStoreCollection = (params: AddStoreCollectionParams) => postToAPI('addStoreCollection', params) as Promise<AddStoreCollectionResult>;

export type AddStoreProductToCartParams = { design: string; size?: Maybe<string>; quantity?: Maybe<number>; options?: Maybe<{ productTemplate?: string; }>; name?: Maybe<string>; alias?: Maybe<string>; number?: Maybe<string>; flag?: Maybe<string>; };
export type AddStoreProductToCartResult = { id: string; };
export const addStoreProductToCart = (params: AddStoreProductToCartParams) => postToAPI('addStoreProductToCart', params) as Promise<AddStoreProductToCartResult>;

export type AddStorePromocodeParams = { id: string; store: string; percent_discount: number; expires?: Maybe<string>; };
export type AddStorePromocodeResult = { success: boolean; };
export const addStorePromocode = (params: AddStorePromocodeParams) => postToAPI('addStorePromocode', params) as Promise<AddStorePromocodeResult>;

export type ApplyPromoCodeParams = { cart: string; code: string; };
export type ApplyPromoCodeResult = { cart: Cart; };
export const applyPromoCode = (params: ApplyPromoCodeParams) => postToAPI('applyPromoCode', params) as Promise<ApplyPromoCodeResult>;

export type AssignPrintFileFromDSTParams = { item: number; secret: string; dst: File; preview: File; area?: Maybe<string>; };
export type AssignPrintFileFromDSTResult = { printFile: PrintFile; item: CartProductItem<OrderItem>; };
export const assignPrintFileFromDST = (params: AssignPrintFileFromDSTParams) => postToAPI('assignPrintFileFromDST', params) as Promise<AssignPrintFileFromDSTResult>;

export type AuthenticateStoreCollectionParams = { collection: string; passphrase: string; };
export type AuthenticateStoreCollectionResult = { success: boolean; };
export const authenticateStoreCollection = (params: AuthenticateStoreCollectionParams) => postToAPI('authenticateStoreCollection', params) as Promise<AuthenticateStoreCollectionResult>;

export type BeginEmailAuthParams = { email: string; acceptAccount?: Maybe<string>; };
export type BeginEmailAuthResult = AuthResponse;
export const beginEmailAuth = (params: BeginEmailAuthParams) => postToAPI('beginEmailAuth', params) as Promise<BeginEmailAuthResult>;

export type CalculateDesignPriceParams = { product: string; design?: Maybe<string>; parts?: Maybe<{ [_: string]: unknown }>; roster?: Maybe<unknown[]>; };
export type CalculateDesignPriceResult = { breakdown: DesignPriceBreakdown; };
export const calculateDesignPrice = (params: CalculateDesignPriceParams) => postToAPI('calculateDesignPrice', params) as Promise<CalculateDesignPriceResult>;

export type CalculateDesignPriceForStoreParams = { product: string; store: string; design?: Maybe<string>; parts?: Maybe<{ [_: string]: unknown }>; };
export type CalculateDesignPriceForStoreResult = { msrp: DesignPriceBreakdown; breakdown: DesignPriceBreakdown; allowedInStore?: Maybe<boolean>; smartPrice: Decimal | undefined; };
export const calculateDesignPriceForStore = (params: CalculateDesignPriceForStoreParams) => postToAPI('calculateDesignPriceForStore', params) as Promise<CalculateDesignPriceForStoreResult>;

export type CalculateUploadColorsParams = { upload: string; profile?: Maybe<string>; };
export type CalculateUploadColorsResult = { colors: ColorTuple[]; };
export const calculateUploadColors = (params: CalculateUploadColorsParams) => postToAPI('calculateUploadColors', params) as Promise<CalculateUploadColorsResult>;

export type CancelPreparedOrderParams = { order: number; };
export type CancelPreparedOrderResult = { success: boolean; };
export const cancelPreparedOrder = (params: CancelPreparedOrderParams) => postToAPI('cancelPreparedOrder', params) as Promise<CancelPreparedOrderResult>;

export type CancelPromoCodeParams = { cart: string; code: string; };
export type CancelPromoCodeResult = { cart: Cart; };
export const cancelPromoCode = (params: CancelPromoCodeParams) => postToAPI('cancelPromoCode', params) as Promise<CancelPromoCodeResult>;

export type CapturePaypalPaymentParams = { payment: string; paypalOrder: string; };
export type CapturePaypalPaymentResult = { success: boolean; };
export const capturePaypalPayment = (params: CapturePaypalPaymentParams) => postToAPI('capturePaypalPayment', params) as Promise<CapturePaypalPaymentResult>;

export type ChangeCollectionSortParams = { store: string; collection: string; end: number; };
export type ChangeCollectionSortResult = { collections: StoreCollection[]; };
export const changeCollectionSort = (params: ChangeCollectionSortParams) => postToAPI('changeCollectionSort', params) as Promise<ChangeCollectionSortResult>;

export type ChangeDesignSortParams = { store: string; design: string; end: number; };
export type ChangeDesignSortResult = { success: boolean; };
export const changeDesignSort = (params: ChangeDesignSortParams) => postToAPI('changeDesignSort', params) as Promise<ChangeDesignSortResult>;

export type CleanUpStoreDraftParams = { store: string; };
export type CleanUpStoreDraftResult = { success: boolean; };
export const cleanUpStoreDraft = (params: CleanUpStoreDraftParams) => postToAPI('cleanUpStoreDraft', params) as Promise<CleanUpStoreDraftResult>;

export type CompleteStripeSetupIntentParams = { id: string; };
export type CompleteStripeSetupIntentResult = { success: boolean; };
export const completeStripeSetupIntent = (params: CompleteStripeSetupIntentParams) => postToAPI('completeStripeSetupIntent', params) as Promise<CompleteStripeSetupIntentResult>;

export type ConvertAccountToTeamResult = { account: OwnAccount; };
export const convertAccountToTeam = () => postToAPI('convertAccountToTeam') as Promise<ConvertAccountToTeamResult>;

export type ConvertColorsParams = { colors: number[][]; profile: string; };
export type ConvertColorsResult = { colors: ColorTuple[]; };
export const convertColors = (params: ConvertColorsParams) => postToAPI('convertColors', params) as Promise<ConvertColorsResult>;

export type ConvertColorsFromCMYKParams = { colors: number[][]; profile: string; };
export type ConvertColorsFromCMYKResult = { colors: ColorTuple[]; };
export const convertColorsFromCMYK = (params: ConvertColorsFromCMYKParams) => postToAPI('convertColorsFromCMYK', params) as Promise<ConvertColorsFromCMYKResult>;

export type CopyItemsToActiveCartParams = { cart: string; };
export type CopyItemsToActiveCartResult = { cart: Cart; };
export const copyItemsToActiveCart = (params: CopyItemsToActiveCartParams) => postToAPI('copyItemsToActiveCart', params) as Promise<CopyItemsToActiveCartResult>;

export type CopyRosterParams = { id: number; };
export type CopyRosterResult = { roster: SavedRoster; };
export const copyRoster = (params: CopyRosterParams) => postToAPI('copyRoster', params) as Promise<CopyRosterResult>;

export type CopySavedCartParams = { cart: string; };
export type CopySavedCartResult = { cart: Cart; };
export const copySavedCart = (params: CopySavedCartParams) => postToAPI('copySavedCart', params) as Promise<CopySavedCartResult>;

export type CreateAccountParams = { name: string; email: string; owner?: Maybe<string>; salesRep?: Maybe<string>; };
export type CreateAccountResult = { account: OwnAccount; };
export const createAccount = (params: CreateAccountParams) => postToAPI('createAccount', params) as Promise<CreateAccountResult>;

export type CreateCartParams = Maybe<{ name?: Maybe<string>; }> | undefined;
export type CreateCartResult = { cart: Cart; };
export const createCart = (params?: CreateCartParams) => postToAPI('createCart', params) as Promise<CreateCartResult>;

export type CreateGiftCardForPayoutParams = { amount: Decimal; };
export type CreateGiftCardForPayoutResult = { giftCard: string; };
export const createGiftCardForPayout = (params: CreateGiftCardForPayoutParams) => postToAPI('createGiftCardForPayout', params) as Promise<CreateGiftCardForPayoutResult>;

export type CreateOrderFromCartParams = { cart: string; amount: string; special?: undefined | null | ('prepare' | 'budget' | 'free'); confirmRepeatOrder?: Maybe<boolean>; currency?: Maybe<CurrencyId>; };
export type CreateOrderFromCartResult = { previousOrder?: undefined | null; order: Order; remaining: string; secret: string; payment?: Maybe<string>; } | { previousOrder: Order; order?: undefined | null; remaining?: undefined | null; secret?: undefined | null; payment?: undefined | null; };
export const createOrderFromCart = (params: CreateOrderFromCartParams) => postToAPI('createOrderFromCart', params) as Promise<CreateOrderFromCartResult>;

export type CreatePaypalPaymentParams = { breakdown: { order: number; type: string; amount: Decimal; }[]; currency?: Maybe<CurrencyId>; };
export type CreatePaypalPaymentResult = { payment: Payment; paypalOrder: string; };
export const createPaypalPayment = (params: CreatePaypalPaymentParams) => postToAPI('createPaypalPayment', params) as Promise<CreatePaypalPaymentResult>;

export type CreateShortlinkParams = { link: string; promocode?: Maybe<string>; };
export type CreateShortlinkResult = { shortlink: Shortlink; link: string; };
export const createShortlink = (params: CreateShortlinkParams) => postToAPI('createShortlink', params) as Promise<CreateShortlinkResult>;

export type CreateStoreParams = { name: string; };
export type CreateStoreResult = { store: Store; };
export const createStore = (params: CreateStoreParams) => postToAPI('createStore', params) as Promise<CreateStoreResult>;

export type CreateStorePaymentParams = { breakdown: { order: number; type: string; amount: Decimal; }[]; store: string; currency?: Maybe<CurrencyId>; };
export type CreateStorePaymentResult = { payment: string; };
export const createStorePayment = (params: CreateStorePaymentParams) => postToAPI('createStorePayment', params) as Promise<CreateStorePaymentResult>;

export type CreateStripePaymentParams = { breakdown: { order: number; type: string; amount: Decimal; }[]; method?: Maybe<string>; currency?: Maybe<CurrencyId>; };
export type CreateStripePaymentResult = { completed: boolean; intentSecret?: Maybe<string>; canSaveCard: boolean; payment: string; };
export const createStripePayment = (params: CreateStripePaymentParams) => postToAPI('createStripePayment', params) as Promise<CreateStripePaymentResult>;

export type CreateStripeSetupIntentResult = { secret: string; };
export const createStripeSetupIntent = () => postToAPI('createStripeSetupIntent') as Promise<CreateStripeSetupIntentResult>;

export type CreateUserPurchaseOrderParams = { order: number; upload?: Maybe<string>; number: string; };
export type CreateUserPurchaseOrderResult = { order: number; };
export const createUserPurchaseOrder = (params: CreateUserPurchaseOrderParams) => postToAPI('createUserPurchaseOrder', params) as Promise<CreateUserPurchaseOrderResult>;

export type DeleteAccountAddressParams = { address: string; };
export type DeleteAccountAddressResult = { success: boolean; };
export const deleteAccountAddress = (params: DeleteAccountAddressParams) => postToAPI('deleteAccountAddress', params) as Promise<DeleteAccountAddressResult>;

export type DeleteCartParams = { id: string; };
export type DeleteCartResult = {};
export const deleteCart = (params: DeleteCartParams) => postToAPI('deleteCart', params) as Promise<DeleteCartResult>;

export type DeleteSavedDesignParams = { design: string; };
export type DeleteSavedDesignResult = { success: boolean; };
export const deleteSavedDesign = (params: DeleteSavedDesignParams) => postToAPI('deleteSavedDesign', params) as Promise<DeleteSavedDesignResult>;

export type DeleteSavedPaymentMethodParams = { method: string; };
export type DeleteSavedPaymentMethodResult = { success: boolean; };
export const deleteSavedPaymentMethod = (params: DeleteSavedPaymentMethodParams) => postToAPI('deleteSavedPaymentMethod', params) as Promise<DeleteSavedPaymentMethodResult>;

export type DeleteStoreCollectionParams = { collection: string; };
export type DeleteStoreCollectionResult = { success: boolean; };
export const deleteStoreCollection = (params: DeleteStoreCollectionParams) => postToAPI('deleteStoreCollection', params) as Promise<DeleteStoreCollectionResult>;

export type DeleteStoreProductParams = { design: string; };
export type DeleteStoreProductResult = { success: boolean; };
export const deleteStoreProduct = (params: DeleteStoreProductParams) => postToAPI('deleteStoreProduct', params) as Promise<DeleteStoreProductResult>;

export type DeleteUploadParams = { upload: string; };
export type DeleteUploadResult = { success: boolean; };
export const deleteUpload = (params: DeleteUploadParams) => postToAPI('deleteUpload', params) as Promise<DeleteUploadResult>;

export type EnterPasswordParams = { cookie: string; password: string; };
export type EnterPasswordResult = AuthResponse;
export const enterPassword = (params: EnterPasswordParams) => postToAPI('enterPassword', params) as Promise<EnterPasswordResult>;

export type GetAccountAddressParams = { address: string; };
export type GetAccountAddressResult = { address: Address; };
export const getAccountAddress = (params: GetAccountAddressParams) => getFromAPI('getAccountAddress', params) as Promise<GetAccountAddressResult>;

export type GetAccountPayoutsParams = Maybe<{ startDate?: Maybe<string>; endDate?: Maybe<string>; }> | undefined;
export type GetAccountPayoutsResult = { openingBalance: PayoutsSummaryRow; filteredBalance: PayoutsSummaryRow; filteredRows: PayoutsSummaryRow[]; currentBalance: PayoutsSummaryRow; closingBalance: PayoutsSummaryRow; giftCardValue: Decimal; giftCardMultiplier: number; tiers: Tier[]; };
export const getAccountPayouts = (params?: GetAccountPayoutsParams) => getFromAPI('getAccountPayouts', params) as Promise<GetAccountPayoutsResult>;

export type GetAccountRelationsResult = { league?: Maybe<PublicAccount>; salesRep?: Maybe<PublicAccount>; supportRep?: Maybe<PublicAccount>; };
export const getAccountRelations = () => getFromAPI('getAccountRelations') as Promise<GetAccountRelationsResult>;

export type GetAccountStoreParams = { id: string; provide?: Maybe<boolean>; needDesigns?: Maybe<boolean>; category?: Maybe<string>; };
export type GetAccountStoreResult = { store: Store; tier: Tier; designs?: Maybe<StoreProduct[]>; categories: ProductCategory[]; haveDraft: boolean; };
export const getAccountStore = (params: GetAccountStoreParams) => getFromAPI('getAccountStore', params) as Promise<GetAccountStoreResult>;

export type GetAllFeedbacksResult = { feedbacks: OrderItemFeedbackEntry[]; };
export const getAllFeedbacks = () => getFromAPI('getAllFeedbacks') as Promise<GetAllFeedbacksResult>;

export type GetAvailableAuthServicesResult = { services: string[]; };
export const getAvailableAuthServices = () => getFromAPI('getAvailableAuthServices') as Promise<GetAvailableAuthServicesResult>;

export type GetCartParams = { id: string; };
export type GetCartResult = { cart: Cart; };
export const getCart = (params: GetCartParams) => getFromAPI('getCart', params) as Promise<GetCartResult>;

export type GetCollectionsParams = { store: string; isLive?: Maybe<boolean>; };
export type GetCollectionsResult = { collections: StoreCollection[]; };
export const getCollections = (params: GetCollectionsParams) => getFromAPI('getCollections', params) as Promise<GetCollectionsResult>;

export type GetColorPresetsForProfileParams = { profile: string; };
export type GetColorPresetsForProfileResult = { colorPresets: ColorTuple[][]; };
export const getColorPresetsForProfile = (params: GetColorPresetsForProfileParams) => getFromAPI('getColorPresetsForProfile', params) as Promise<GetColorPresetsForProfileResult>;

export type GetCommandsForUploadParams = { upload: string; };
export type GetCommandsForUploadResult = { upload: Upload; };
export const getCommandsForUpload = (params: GetCommandsForUploadParams) => getFromAPI('getCommandsForUpload', params) as Promise<GetCommandsForUploadResult>;

export type GetContextUpdatesParams = Maybe<{ stateHash?: Maybe<string>; }> | undefined;
export type GetContextUpdatesResult = { counts: UnreadMessageCounts; state?: Maybe<{ user: OwnUser; account: OwnAccount; accountAccess: AccountAccessData; accountStores: Store[]; accounts: AccountAccessData[]; stateHash: string; }>; };
export const getContextUpdates = (params?: GetContextUpdatesParams) => getFromAPI('getContextUpdates', params) as Promise<GetContextUpdatesResult>;

export type GetCurrentCartParams = Maybe<{ fetchShippingOptions?: Maybe<boolean>; }> | undefined;
export type GetCurrentCartResult = { cart: Maybe<Cart>; };
export const getCurrentCart = (params?: GetCurrentCartParams) => getFromAPI('getCurrentCart', params) as Promise<GetCurrentCartResult>;

export type GetDashboardCommissionsInfoResult = { tiers: Tier[]; groups: TierCategoryGroup[]; categories: TierCategory[]; products: DashboardCommissionsProduct[]; dtfBasePrice: Decimal; embBasePrice: Decimal; embMultiplier: Decimal; };
export const getDashboardCommissionsInfo = () => getFromAPI('getDashboardCommissionsInfo') as Promise<GetDashboardCommissionsInfoResult>;

export type GetDashboardInvoicesResult = { orders: Order[]; total: Decimal; unpaid: Decimal; limit: Decimal; };
export const getDashboardInvoices = () => getFromAPI('getDashboardInvoices') as Promise<GetDashboardInvoicesResult>;

export type GetDashboardPaymentsResult = { payments: Payment[]; };
export const getDashboardPayments = () => getFromAPI('getDashboardPayments') as Promise<GetDashboardPaymentsResult>;

export type GetDashboardPayoutsInfoResult = { total: Decimal; earnings: number; days: number | null; };
export const getDashboardPayoutsInfo = () => getFromAPI('getDashboardPayoutsInfo') as Promise<GetDashboardPayoutsInfoResult>;

export type GetDashboardPurchasesParams = Maybe<{ status?: Maybe<string>; sort?: Maybe<string>; id?: Maybe<number>; }> | undefined;
export type GetDashboardPurchasesResult = { orderSummaries: OrderSummary[]; feedbacks: { [_: number]: OrderItemFeedbackEntry }; photos: { [_: number]: OrderItemPhoto[] }; };
export const getDashboardPurchases = (params?: GetDashboardPurchasesParams) => getFromAPI('getDashboardPurchases', params) as Promise<GetDashboardPurchasesResult>;

export type GetDashboardRollingTotalsResult = { tiers: Tier[]; currentRolling: boolean; nextRolling: boolean; rows: { date: string; start_date: string; date_total: Decimal; month_total: Decimal; rolling_total: Decimal; all_time_total: Decimal; tier: number; to_current_tier?: Maybe<Decimal>; to_next_tier?: Maybe<Decimal>; }[]; };
export const getDashboardRollingTotals = () => getFromAPI('getDashboardRollingTotals') as Promise<GetDashboardRollingTotalsResult>;

export type GetDashboardUserCommissionsResult = { earnings: CommissionsEntry[]; salesTable: SalesInfo[]; };
export const getDashboardUserCommissions = () => getFromAPI('getDashboardUserCommissions') as Promise<GetDashboardUserCommissionsResult>;

export type GetDesignsByCategoryParams = { store: string; cid?: Maybe<string>; };
export type GetDesignsByCategoryResult = { designs: StoreProduct[]; };
export const getDesignsByCategory = (params: GetDesignsByCategoryParams) => getFromAPI('getDesignsByCategory', params) as Promise<GetDesignsByCategoryResult>;

export type GetDesignsByCollectionParams = { store: string; collection?: Maybe<string>; draft?: Maybe<boolean>; noCollection?: Maybe<boolean>; };
export type GetDesignsByCollectionResult = { designs: StoreProduct[]; };
export const getDesignsByCollection = (params: GetDesignsByCollectionParams) => getFromAPI('getDesignsByCollection', params) as Promise<GetDesignsByCollectionResult>;

export type GetDesignsOrderBySortParams = { store: string; cid?: Maybe<string>; };
export type GetDesignsOrderBySortResult = { designs: StoreProduct[]; };
export const getDesignsOrderBySort = (params: GetDesignsOrderBySortParams) => getFromAPI('getDesignsOrderBySort', params) as Promise<GetDesignsOrderBySortResult>;

export type GetDimensionByIdParams = { id: string; };
export type GetDimensionByIdResult = { dimension: ProductCategoryDimension; };
export const getDimensionById = (params: GetDimensionByIdParams) => getFromAPI('getDimensionById', params) as Promise<GetDimensionByIdResult>;

export type GetMemberStatsParams = Maybe<{ startDate?: Maybe<string>; endDate?: Maybe<string>; }> | undefined;
export type GetMemberStatsResult = { members: MemberStatsRow[]; };
export const getMemberStats = (params?: GetMemberStatsParams) => getFromAPI('getMemberStats', params) as Promise<GetMemberStatsResult>;

export type GetMessageParams = { id: number; acknowledge?: Maybe<boolean>; };
export type GetMessageResult = { message: Message; };
export const getMessage = (params: GetMessageParams) => getFromAPI('getMessage', params) as Promise<GetMessageResult>;

export type GetMessagesParams = undefined | null | { recipient?: undefined | null | ('user' | 'account' | 'any' | 'all'); unseenOnly?: Maybe<boolean>; markSeen?: Maybe<boolean>; beforeId?: Maybe<number>; order?: Maybe<number>; store?: Maybe<string>; category?: Maybe<MessageCategory>; type?: Maybe<string>; withContent?: Maybe<boolean>; limit?: Maybe<number>; };
export type GetMessagesResult = { messages: Message[]; haveMore: boolean; minId?: Maybe<number>; };
export const getMessages = (params?: GetMessagesParams) => getFromAPI('getMessages', params) as Promise<GetMessagesResult>;

export type GetMyUploadsParams = Maybe<{ filter?: Maybe<string>; }> | undefined;
export type GetMyUploadsResult = { uploads: Upload[]; };
export const getMyUploads = (params?: GetMyUploadsParams) => getFromAPI('getMyUploads', params) as Promise<GetMyUploadsResult>;

export type GetOrderInfoParams = { oid: string; };
export type GetOrderInfoResult = { canReOrder: string[]; order: Order; billing: Address; shipping: Address; items: OrderItemsTracking[]; };
export const getOrderInfo = (params: GetOrderInfoParams) => getFromAPI('getOrderInfo', params) as Promise<GetOrderInfoResult>;

export type GetOrderItemFeedbackParams = { item: number; };
export type GetOrderItemFeedbackResult = { feedBack: OrderItemFeedbackEntry; item: OrderItem; order: Order; photos: OrderItemPhoto[]; contest?: Maybe<string>; reviewLinks: { [_: string]: string }; };
export const getOrderItemFeedback = (params: GetOrderItemFeedbackParams) => getFromAPI('getOrderItemFeedback', params) as Promise<GetOrderItemFeedbackResult>;

export type GetOwnStoreProductParams = { id: string; };
export type GetOwnStoreProductResult = { storeProduct: StoreProduct; design: Design; };
export const getOwnStoreProduct = (params: GetOwnStoreProductParams) => getFromAPI('getOwnStoreProduct', params) as Promise<GetOwnStoreProductResult>;

export type GetPaymentOptionsParams = Maybe<{ cart?: Maybe<string>; order?: Maybe<number>; }> | undefined;
export type GetPaymentOptionsResult = { options: PaymentOption[]; };
export const getPaymentOptions = (params?: GetPaymentOptionsParams) => getFromAPI('getPaymentOptions', params) as Promise<GetPaymentOptionsResult>;

export type GetPopularColorsForProfileParams = { profile: string; };
export type GetPopularColorsForProfileResult = { colors: ColorTuple[]; };
export const getPopularColorsForProfile = (params: GetPopularColorsForProfileParams) => getFromAPI('getPopularColorsForProfile', params) as Promise<GetPopularColorsForProfileResult>;

export type GetPreviousColorMappingForUploadParams = { upload: string; decoration: string; };
export type GetPreviousColorMappingForUploadResult = { threads?: Maybe<{ [_: string]: Maybe<string | undefined > }>; colors?: Maybe<{ [_: string]: Maybe<number[] | undefined > }>; };
export const getPreviousColorMappingForUpload = (params: GetPreviousColorMappingForUploadParams) => getFromAPI('getPreviousColorMappingForUpload', params) as Promise<GetPreviousColorMappingForUploadResult>;

export type GetProductParams = { product: string; };
export type GetProductResult = { product: FullPublicProduct; flow: PublicFlow; };
export const getProduct = (params: GetProductParams) => getFromAPI('getProduct', params) as Promise<GetProductResult>;

export type GetProductCategoriesParams = Maybe<{ allDomains?: Maybe<boolean>; }> | undefined;
export type GetProductCategoriesResult = { categories: ProductCategory[]; };
export const getProductCategories = (params?: GetProductCategoriesParams) => getFromAPI('getProductCategories', params) as Promise<GetProductCategoriesResult>;

export type GetProductionTimesResult = { prodTimes: ProductionTimeEntry[]; };
export const getProductionTimes = () => getFromAPI('getProductionTimes') as Promise<GetProductionTimesResult>;

export type GetProductsByGraphicsParams = Maybe<{ graphics?: Maybe<string>; }> | undefined;
export type GetProductsByGraphicsResult = { products: ComputedProduct[]; };
export const getProductsByGraphics = (params?: GetProductsByGraphicsParams) => getFromAPI('getProductsByGraphics', params) as Promise<GetProductsByGraphicsResult>;

export type GetProductsWithSizeChartsResult = { products: PublicProduct[]; };
export const getProductsWithSizeCharts = () => getFromAPI('getProductsWithSizeCharts') as Promise<GetProductsWithSizeChartsResult>;

export type GetProductTemplateParams = { id: string; };
export type GetProductTemplateResult = { productTemplate: ProductTemplate; };
export const getProductTemplate = (params: GetProductTemplateParams) => getFromAPI('getProductTemplate', params) as Promise<GetProductTemplateResult>;

export type GetSalesOrdersParams = { storeId: string; startDate?: Maybe<string>; endDate?: Maybe<string>; };
export type GetSalesOrdersResult = { filteredRows: SalesOrder[]; };
export const getSalesOrders = (params: GetSalesOrdersParams) => getFromAPI('getSalesOrders', params) as Promise<GetSalesOrdersResult>;

export type GetSavedCartsResult = { carts: Cart[]; };
export const getSavedCarts = () => getFromAPI('getSavedCarts') as Promise<GetSavedCartsResult>;

export type GetSavedDesignParams = { id: string; };
export type GetSavedDesignResult = { savedDesign: SavedDesign; design: Design; };
export const getSavedDesign = (params: GetSavedDesignParams) => getFromAPI('getSavedDesign', params) as Promise<GetSavedDesignResult>;

export type GetSavedDesignsParams = Maybe<{ category?: Maybe<string>; }> | undefined;
export type GetSavedDesignsResult = { designs: SavedDesign[]; };
export const getSavedDesigns = (params?: GetSavedDesignsParams) => getFromAPI('getSavedDesigns', params) as Promise<GetSavedDesignsResult>;

export type GetSavedRosterParams = { id: number; };
export type GetSavedRosterResult = { roster: SavedRoster; };
export const getSavedRoster = (params: GetSavedRosterParams) => getFromAPI('getSavedRoster', params) as Promise<GetSavedRosterResult>;

export type GetSavedRostersResult = { rosters: SavedRoster[]; };
export const getSavedRosters = () => getFromAPI('getSavedRosters') as Promise<GetSavedRostersResult>;

export type GetShortlinkDataResult = { shortlinks: ShortlinkData[]; tiers: Tier[]; };
export const getShortlinkData = () => getFromAPI('getShortlinkData') as Promise<GetShortlinkDataResult>;

export type GetSizeChartParams = { product: string; };
export type GetSizeChartResult = { size_chart: SizeChart; };
export const getSizeChart = (params: GetSizeChartParams) => getFromAPI('getSizeChart', params) as Promise<GetSizeChartResult>;

export type GetStoreProductStatsParams = undefined | null | { storeId?: Maybe<string>; groupBy?: undefined | null | ('storeProduct' | 'baseProduct'); startDate?: Maybe<string>; endDate?: Maybe<string>; };
export type GetStoreProductStatsResult = { filteredRows: StoreProductStatsRow[]; };
export const getStoreProductStats = (params?: GetStoreProductStatsParams) => getFromAPI('getStoreProductStats', params) as Promise<GetStoreProductStatsResult>;

export type GetStorePromocodesParams = { store: string; };
export type GetStorePromocodesResult = { maxPercentDiscount: number; includesOptions: PromocodeIncludesOptions; promocodes: PromocodeWithStats[]; };
export const getStorePromocodes = (params: GetStorePromocodesParams) => getFromAPI('getStorePromocodes', params) as Promise<GetStorePromocodesResult>;

export type GetStoreUploadsParams = { store: string; };
export type GetStoreUploadsResult = { uploads: Upload[]; };
export const getStoreUploads = (params: GetStoreUploadsParams) => getFromAPI('getStoreUploads', params) as Promise<GetStoreUploadsResult>;

export type GetTemplateParams = { id: string; };
export type GetTemplateResult = { template: Template; };
export const getTemplate = (params: GetTemplateParams) => getFromAPI('getTemplate', params) as Promise<GetTemplateResult>;

export type GetUnpaidRastersParams = { store: string; };
export type GetUnpaidRastersResult = { unpaids: StoreUploadsService[]; };
export const getUnpaidRasters = (params: GetUnpaidRastersParams) => getFromAPI('getUnpaidRasters', params) as Promise<GetUnpaidRastersResult>;

export type GetUnreadMessageCountsResult = { counts: UnreadMessageCounts; };
export const getUnreadMessageCounts = () => getFromAPI('getUnreadMessageCounts') as Promise<GetUnreadMessageCountsResult>;

export type GetUploadParams = { upload: string; };
export type GetUploadResult = { upload: Upload; };
export const getUpload = (params: GetUploadParams) => getFromAPI('getUpload', params) as Promise<GetUploadResult>;

export type GetUserInfoResult = { user: OwnUser; };
export const getUserInfo = () => getFromAPI('getUserInfo') as Promise<GetUserInfoResult>;

export type GetVendorPurchaseOrderParams = { id: number; vendor: string; };
export type GetVendorPurchaseOrderResult = { vpo: VendorPurchaseOrder; };
export const getVendorPurchaseOrder = (params: GetVendorPurchaseOrderParams) => getFromAPI('getVendorPurchaseOrder', params) as Promise<GetVendorPurchaseOrderResult>;

export type GetVendorPurchaseOrderItemsParams = { vendor_purchase_order: number; };
export type GetVendorPurchaseOrderItemsResult = { items: OrderItem[]; };
export const getVendorPurchaseOrderItems = (params: GetVendorPurchaseOrderItemsParams) => getFromAPI('getVendorPurchaseOrderItems', params) as Promise<GetVendorPurchaseOrderItemsResult>;

export type GetVendorPurchaseOrdersParams = { vendor: string; };
export type GetVendorPurchaseOrdersResult = { vendorPurchaseOrders: VendorPurchaseOrder[]; };
export const getVendorPurchaseOrders = (params: GetVendorPurchaseOrdersParams) => getFromAPI('getVendorPurchaseOrders', params) as Promise<GetVendorPurchaseOrdersResult>;

export type JoinLeagueParams = { league: string; secret: string; };
export type JoinLeagueResult = { redirect: string; success: string; };
export const joinLeague = (params: JoinLeagueParams) => postToAPI('joinLeague', params) as Promise<JoinLeagueResult>;

export type LeaveLeagueParams = { league: string; };
export type LeaveLeagueResult = { success: boolean; };
export const leaveLeague = (params: LeaveLeagueParams) => postToAPI('leaveLeague', params) as Promise<LeaveLeagueResult>;

export type ListAccountAccessParams = Maybe<{ usersOnly?: Maybe<boolean>; }> | undefined;
export type ListAccountAccessResult = { entries: AccountAccess[]; accessible: AccountAccessData[]; };
export const listAccountAccess = (params?: ListAccountAccessParams) => getFromAPI('listAccountAccess', params) as Promise<ListAccountAccessResult>;

export type ListAccountAddressesResult = { addresses: Address[]; };
export const listAccountAddresses = () => getFromAPI('listAccountAddresses') as Promise<ListAccountAddressesResult>;

export type ListBudgetsResult = { entries: AccountAccess[]; };
export const listBudgets = () => getFromAPI('listBudgets') as Promise<ListBudgetsResult>;

export type ListOtherApparelProductsParams = { product: string; template?: Maybe<string>; };
export type ListOtherApparelProductsResult = { rows: { product: (PublicProduct & WithFeatures & ComputedProductInfo); flow: PublicFlow; product_template: ProductTemplate; product_template_inventory: ProductTemplateInventory; match: boolean; }[]; };
export const listOtherApparelProducts = (params: ListOtherApparelProductsParams) => getFromAPI('listOtherApparelProducts', params) as Promise<ListOtherApparelProductsResult>;

export type ListProductsParams = undefined | null | { filter?: Maybe<{ [_: string]: Maybe<string[] | undefined > }>; query?: Maybe<string>; sort?: Maybe<ProductSortType>; };
export type ListProductsResult = { products: ComputedProduct[]; validCategories: string[]; };
export const listProducts = (params?: ListProductsParams) => getFromAPI('listProducts', params) as Promise<ListProductsResult>;

export type ListProductTemplatesParams = { product: string; require?: Maybe<string>; };
export type ListProductTemplatesResult = { productTemplates: ProductTemplate[]; };
export const listProductTemplates = (params: ListProductTemplatesParams) => getFromAPI('listProductTemplates', params) as Promise<ListProductTemplatesResult>;

export type ListSavedPaymentMethodsResult = { methods: PaymentMethod[]; giftCards: Promocode[]; stripeKey?: Maybe<string>; canEditAll: boolean; creditLimit: CreditLimitTotals; storeCredit: Decimal; storeSponsorship: Decimal; };
export const listSavedPaymentMethods = () => getFromAPI('listSavedPaymentMethods') as Promise<ListSavedPaymentMethodsResult>;

export const logOut = () => postToAPI('logOut') as Promise<any>;

export type MarkMessagesSeenParams = { messages: number[]; };
export type MarkMessagesSeenResult = { success: boolean; };
export const markMessagesSeen = (params: MarkMessagesSeenParams) => postToAPI('markMessagesSeen', params) as Promise<MarkMessagesSeenResult>;

export type ObtainAccountAccessParams = { account: string; asAdmin?: Maybe<boolean>; };
export type ObtainAccountAccessResult = { host: string; url: string; };
export const obtainAccountAccess = (params: ObtainAccountAccessParams) => postToAPI('obtainAccountAccess', params) as Promise<ObtainAccountAccessResult>;

export type PublishStoreParams = { store: string; };
export type PublishStoreResult = { unpaids?: Maybe<boolean>; success?: Maybe<boolean>; };
export const publishStore = (params: PublishStoreParams) => postToAPI('publishStore', params) as Promise<PublishStoreResult>;

export type RecoverStoreCollectionParams = { collection: string; };
export type RecoverStoreCollectionResult = { success: boolean; };
export const recoverStoreCollection = (params: RecoverStoreCollectionParams) => postToAPI('recoverStoreCollection', params) as Promise<RecoverStoreCollectionResult>;

export type RecoverStoreProductParams = { design: string; };
export type RecoverStoreProductResult = { success: boolean; };
export const recoverStoreProduct = (params: RecoverStoreProductParams) => postToAPI('recoverStoreProduct', params) as Promise<RecoverStoreProductResult>;

export type RejectAccountInviteParams = { message: number; };
export type RejectAccountInviteResult = { success: boolean; };
export const rejectAccountInvite = (params: RejectAccountInviteParams) => postToAPI('rejectAccountInvite', params) as Promise<RejectAccountInviteResult>;

export type RelinquishAccountAccessParams = { id: number; };
export type RelinquishAccountAccessResult = { accounts: AccountAccessData[]; };
export const relinquishAccountAccess = (params: RelinquishAccountAccessParams) => postToAPI('relinquishAccountAccess', params) as Promise<RelinquishAccountAccessResult>;

export type RemoveAccountAccessParams = { accountAccess: number; };
export type RemoveAccountAccessResult = { success: boolean; };
export const removeAccountAccess = (params: RemoveAccountAccessParams) => postToAPI('removeAccountAccess', params) as Promise<RemoveAccountAccessResult>;

export type RemoveItemPhotoParams = { id: string; };
export type RemoveItemPhotoResult = { success: boolean; };
export const removeItemPhoto = (params: RemoveItemPhotoParams) => postToAPI('removeItemPhoto', params) as Promise<RemoveItemPhotoResult>;

export type RemoveLeagueMemberParams = { member: string; };
export type RemoveLeagueMemberResult = { success: boolean; };
export const removeLeagueMember = (params: RemoveLeagueMemberParams) => postToAPI('removeLeagueMember', params) as Promise<RemoveLeagueMemberResult>;

export type RemoveRosterParams = { id: number; };
export const removeRoster = (params: RemoveRosterParams) => postToAPI('removeRoster', params) as Promise<any>;

export type ReportEventReportedParams = { event: string; };
export type ReportEventReportedResult = { success: boolean; };
export const reportEventReported = (params: ReportEventReportedParams) => postToAPI('reportEventReported', params) as Promise<ReportEventReportedResult>;

export type RequestRecoveryEmailParams = { cookie: string; };
export type RequestRecoveryEmailResult = AuthResponse;
export const requestRecoveryEmail = (params: RequestRecoveryEmailParams) => postToAPI('requestRecoveryEmail', params) as Promise<RequestRecoveryEmailResult>;

export type ResendVerificationEmailParams = Maybe<{ cookie?: Maybe<string>; email?: Maybe<string>; }> | undefined;
export type ResendVerificationEmailResult = AuthResponse;
export const resendVerificationEmail = (params?: ResendVerificationEmailParams) => postToAPI('resendVerificationEmail', params) as Promise<ResendVerificationEmailResult>;

export type ResetLeagueSecretResult = { account: OwnAccount; };
export const resetLeagueSecret = () => postToAPI('resetLeagueSecret') as Promise<ResetLeagueSecretResult>;

export type SaveDesignParams = { product: string; parts: unknown; previousDesign?: Maybe<string>; roster: unknown; };
export type SaveDesignResult = { id: string; design: Design; };
export const saveDesign = (params: SaveDesignParams) => postToAPI('saveDesign', params) as Promise<SaveDesignResult>;

export type SaveOrderItemFeedbackParams = { item: number; allow_publish?: Maybe<boolean>; public_name?: Maybe<string>; comment?: Maybe<string>; design_rating?: Maybe<Decimal>; print_quality_rating?: Maybe<Decimal>; fabric_rating?: Maybe<Decimal>; time_rating?: Maybe<Decimal>; };
export type SaveOrderItemFeedbackResult = { success: boolean; };
export const saveOrderItemFeedback = (params: SaveOrderItemFeedbackParams) => postToAPI('saveOrderItemFeedback', params) as Promise<SaveOrderItemFeedbackResult>;

export type SaveRosterParams = { id?: Maybe<number>; name?: Maybe<string>; roster: unknown[]; };
export type SaveRosterResult = { rosterId: number; };
export const saveRoster = (params: SaveRosterParams) => postToAPI('saveRoster', params) as Promise<SaveRosterResult>;

export type SelectAccountParams = { cookie?: Maybe<string>; account: string; };
export type SelectAccountResult = AuthResponse;
export const selectAccount = (params: SelectAccountParams) => postToAPI('selectAccount', params) as Promise<SelectAccountResult>;

export type SendCartEstimateEmailParams = { id: string; recipients?: Maybe<string[]>; };
export type SendCartEstimateEmailResult = { success: boolean; };
export const sendCartEstimateEmail = (params: SendCartEstimateEmailParams) => postToAPI('sendCartEstimateEmail', params) as Promise<SendCartEstimateEmailResult>;

export type SetItemPhotoVisibilityParams = { id: string; visible: boolean; };
export type SetItemPhotoVisibilityResult = { success: boolean; };
export const setItemPhotoVisibility = (params: SetItemPhotoVisibilityParams) => postToAPI('setItemPhotoVisibility', params) as Promise<SetItemPhotoVisibilityResult>;

export type SetOrderBillingAddressParams = { id?: Maybe<string>; name_prefix?: Maybe<string>; first_name?: Maybe<string>; middle_name?: Maybe<string>; last_name?: Maybe<string>; name_suffix?: Maybe<string>; company?: Maybe<string>; email?: Maybe<string>; phone?: Maybe<string>; address?: Maybe<string>; address_2?: Maybe<string>; city?: Maybe<string>; region?: Maybe<string>; state?: Maybe<string>; country?: Maybe<string>; postcode?: Maybe<string>; order: number; };
export type SetOrderBillingAddressResult = { address: AddressInfo; };
export const setOrderBillingAddress = (params: SetOrderBillingAddressParams) => postToAPI('setOrderBillingAddress', params) as Promise<SetOrderBillingAddressResult>;

export type SetPasswordParams = { cookie: string; password: string; };
export type SetPasswordResult = AuthResponse;
export const setPassword = (params: SetPasswordParams) => postToAPI('setPassword', params) as Promise<SetPasswordResult>;

export type SetShippingOptionParams = { cart: string; option: string; basis: string; amount: string; };
export type SetShippingOptionResult = { cart: Cart; };
export const setShippingOption = (params: SetShippingOptionParams) => postToAPI('setShippingOption', params) as Promise<SetShippingOptionResult>;

export type StartSocialAuthParams = { method: string; insist?: Maybe<boolean>; };
export type StartSocialAuthResult = { redir: string; };
export const startSocialAuth = (params: StartSocialAuthParams) => postToAPI('startSocialAuth', params) as Promise<StartSocialAuthResult>;

export type SubmitFormParams = { form: string; version: number; answers: { [_: string]: unknown }; };
export type SubmitFormResult = { id: number; };
export const submitForm = (params: SubmitFormParams) => postToAPI('submitForm', params) as Promise<SubmitFormResult>;

export type SubmitUserPurchaseOrderApplicationResult = { success: boolean; };
export const submitUserPurchaseOrderApplication = () => postToAPI('submitUserPurchaseOrderApplication') as Promise<SubmitUserPurchaseOrderApplicationResult>;

export type UnsubscribeByEmailParams = { email: number; userId: string; feedback?: Maybe<string>; };
export type UnsubscribeByEmailResult = { success: boolean; };
export const unsubscribeByEmail = (params: UnsubscribeByEmailParams) => postToAPI('unsubscribeByEmail', params) as Promise<UnsubscribeByEmailResult>;

export type UpdateAccountParams = undefined | null | { name?: Maybe<string>; email?: Maybe<string>; business_name?: Maybe<string>; estimated_annual_volume?: Maybe<string>; tax_state?: Maybe<string>; tax_exempt_proof?: Maybe<string>; requested_net_term_days?: Maybe<string>; requested_limit?: Maybe<string>; avatar?: Maybe<File>; contact_preference?: undefined | null | ('email' | 'phone' | 'discord'); discord_id?: Maybe<string>; org_type?: undefined | null | ('business' | 'esports' | 'sports' | 'creator'); org_structure?: Maybe<string>; sports?: Maybe<string>; games?: Maybe<string>; us_employee_count?: Maybe<string>; us_location_count?: Maybe<string>; rostered_players?: Maybe<string>; merch_needs?: Maybe<string[]>; main_address?: Maybe<{ id?: Maybe<string>; name_prefix?: Maybe<string>; first_name?: Maybe<string>; middle_name?: Maybe<string>; last_name?: Maybe<string>; name_suffix?: Maybe<string>; company?: Maybe<string>; email?: Maybe<string>; phone?: Maybe<string>; address?: Maybe<string>; address_2?: Maybe<string>; city?: Maybe<string>; region?: Maybe<string>; state?: Maybe<string>; country?: Maybe<string>; postcode?: Maybe<string>; }>; links?: Maybe<string>; };
export type UpdateAccountResult = { account: OwnAccount; };
export const updateAccount = (params?: UpdateAccountParams) => postToAPI('updateAccount', params) as Promise<UpdateAccountResult>;

export type UpdateAccountAccessParams = { accountAccess: number; permissions: AccountPermission[]; };
export type UpdateAccountAccessResult = { accountAccess: AccountAccess; };
export const updateAccountAccess = (params: UpdateAccountAccessParams) => postToAPI('updateAccountAccess', params) as Promise<UpdateAccountAccessResult>;

export type UpdateBillingInfoParams = { id?: Maybe<string>; name_prefix?: Maybe<string>; first_name?: Maybe<string>; middle_name?: Maybe<string>; last_name?: Maybe<string>; name_suffix?: Maybe<string>; company?: Maybe<string>; email?: Maybe<string>; phone?: Maybe<string>; address?: Maybe<string>; address_2?: Maybe<string>; city?: Maybe<string>; region?: Maybe<string>; state?: Maybe<string>; country?: Maybe<string>; postcode?: Maybe<string>; cart: string; };
export type UpdateBillingInfoResult = { errors?: Maybe<UserError[]>; cart: Cart; };
export const updateBillingInfo = (params: UpdateBillingInfoParams) => postToAPI('updateBillingInfo', params) as Promise<UpdateBillingInfoResult>;

export type UpdateBudgetsParams = { update: { id?: Maybe<number>; invited_email?: Maybe<string>; budget_limit?: Maybe<Decimal>; budget_since?: Maybe<string>; budget_reset?: Maybe<string>; store_budget_purchase?: Maybe<boolean>; any_budget_purchase?: Maybe<boolean>; auto_approve_budget?: Maybe<boolean>; }[]; autoPaymentMethod?: Maybe<string>; };
export type UpdateBudgetsResult = { errors: { [K in Decimal]?: ApiErrorData }; account?: Maybe<AccountWithAccess>; };
export const updateBudgets = (params: UpdateBudgetsParams) => postToAPI('updateBudgets', params) as Promise<UpdateBudgetsResult>;

export type UpdateCartParams = { id: string; name?: Maybe<string>; comment?: Maybe<string>; deleteOnOrder?: Maybe<boolean>; };
export type UpdateCartResult = { cart: Cart; };
export const updateCart = (params: UpdateCartParams) => postToAPI('updateCart', params) as Promise<UpdateCartResult>;

export type UpdateCartItemsParams = { items: { id: string; design?: Maybe<string>; parts?: Maybe<{ [_: string]: unknown }>; roster?: Maybe<unknown>; quantity?: Maybe<number>; options?: Maybe<unknown>; delete?: Maybe<boolean>; }[]; };
export type UpdateCartItemsResult = { reload?: Maybe<boolean>; cart: Cart; };
export const updateCartItems = (params: UpdateCartItemsParams) => postToAPI('updateCartItems', params) as Promise<UpdateCartItemsResult>;

export type UpdateSavedPaymentMethodParams = { method: string; label?: Maybe<string>; shared?: Maybe<boolean>; };
export type UpdateSavedPaymentMethodResult = { method: PaymentMethod; };
export const updateSavedPaymentMethod = (params: UpdateSavedPaymentMethodParams) => postToAPI('updateSavedPaymentMethod', params) as Promise<UpdateSavedPaymentMethodResult>;

export type UpdateShippingInfoParams = { id?: Maybe<string>; name_prefix?: Maybe<string>; first_name?: Maybe<string>; middle_name?: Maybe<string>; last_name?: Maybe<string>; name_suffix?: Maybe<string>; company?: Maybe<string>; email?: Maybe<string>; phone?: Maybe<string>; address?: Maybe<string>; address_2?: Maybe<string>; city?: Maybe<string>; region?: Maybe<string>; state?: Maybe<string>; country?: Maybe<string>; postcode?: Maybe<string>; cart: string; };
export type UpdateShippingInfoResult = { errors?: Maybe<UserError[]>; cart: Cart; };
export const updateShippingInfo = (params: UpdateShippingInfoParams) => postToAPI('updateShippingInfo', params) as Promise<UpdateShippingInfoResult>;

export type UpdateStoreCollectionParams = { collection: string; name?: Maybe<string>; passphrase?: Maybe<string>; };
export type UpdateStoreCollectionResult = { success: boolean; };
export const updateStoreCollection = (params: UpdateStoreCollectionParams) => postToAPI('updateStoreCollection', params) as Promise<UpdateStoreCollectionResult>;

export type UpdateStoreDraftParams = { store: string; name?: Maybe<string>; description?: Maybe<string>; url?: Maybe<string>; highlight_color?: Maybe<string>; links?: Maybe<string[]>; };
export type UpdateStoreDraftResult = { store: Store; };
export const updateStoreDraft = (params: UpdateStoreDraftParams) => postToAPI('updateStoreDraft', params) as Promise<UpdateStoreDraftResult>;

export type UpdateStoreProductDraftParams = { design: string; name?: Maybe<string>; description?: Maybe<string>; price?: Maybe<number>; options?: Maybe<unknown>; sort?: Maybe<number>; collection?: Maybe<string>; price_locked?: Maybe<boolean>; };
export type UpdateStoreProductDraftResult = { success: boolean; };
export const updateStoreProductDraft = (params: UpdateStoreProductDraftParams) => postToAPI('updateStoreProductDraft', params) as Promise<UpdateStoreProductDraftResult>;

export type UpdateStorePromocodeParams = { promocode: string; update: { active?: boolean; expires?: Maybe<string>; max_uses?: Maybe<number>; max_per_account?: Maybe<number>; include?: [string, string][]; exclude?: [string, string][]; }; };
export type UpdateStorePromocodeResult = { success: boolean; };
export const updateStorePromocode = (params: UpdateStorePromocodeParams) => postToAPI('updateStorePromocode', params) as Promise<UpdateStorePromocodeResult>;

export type UpdateStoreSetupParams = { id: string; name?: Maybe<string>; organization?: Maybe<string>; paypal?: Maybe<string>; url?: Maybe<string>; publishedLinks?: Maybe<string[]>; links?: Maybe<string[]>; banner?: Maybe<File>; display_currency?: Maybe<CurrencyId>; closed?: Maybe<boolean>; };
export type UpdateStoreSetupResult = { success: boolean; };
export const updateStoreSetup = (params: UpdateStoreSetupParams) => postToAPI('updateStoreSetup', params) as Promise<UpdateStoreSetupResult>;

export type UpdateStoreSmartPricingOptionsParams = { store: string; enabled: boolean; update: { lowest_price: boolean; msrp: boolean; dollar_upcharge_msrp: number | null; percentage_upcharge_msrp: number | null; dollar_upcharge_cost: number | null; percentage_upcharge_cost: number | null; round_up_to_99: boolean; round_up_to_00: boolean; }; clearOverrides: boolean; };
export type UpdateStoreSmartPricingOptionsResult = { success: boolean; };
export const updateStoreSmartPricingOptions = (params: UpdateStoreSmartPricingOptionsParams) => postToAPI('updateStoreSmartPricingOptions', params) as Promise<UpdateStoreSmartPricingOptionsResult>;

export type UpdateUserParams = undefined | null | { email?: Maybe<string>; password?: Maybe<string>; message_categories?: undefined | null | { [K in MessageCategory]?: Maybe<{ [K in MessageDeliveryMethod]?: Maybe<boolean | undefined > }> | undefined }; dob?: Maybe<string>; first_name?: Maybe<string>; middle_name?: Maybe<string>; last_name?: Maybe<string>; country?: Maybe<string>; city?: Maybe<string>; state?: Maybe<string>; region?: Maybe<string>; postcode?: Maybe<string>; address?: Maybe<string>; };
export type UpdateUserResult = { success: boolean; };
export const updateUser = (params?: UpdateUserParams) => postToAPI('updateUser', params) as Promise<UpdateUserResult>;

export type UpdateVendorPurchaseOrderParams = { vendor_purchase_order: number; print_date?: Maybe<string>; tracking?: Maybe<string[]>; references?: Maybe<string[]>; delivery_expected?: Maybe<string>; email_subject?: Maybe<string>; email_body?: Maybe<string>; };
export type UpdateVendorPurchaseOrderResult = { success: boolean; };
export const updateVendorPurchaseOrder = (params: UpdateVendorPurchaseOrderParams) => postToAPI('updateVendorPurchaseOrder', params) as Promise<UpdateVendorPurchaseOrderResult>;

export type UpdateVendorPurchaseOrderItemParams = { vendor_purchase_order: number; status: OrderItemStatus; };
export type UpdateVendorPurchaseOrderItemResult = { success: boolean; };
export const updateVendorPurchaseOrderItem = (params: UpdateVendorPurchaseOrderItemParams) => postToAPI('updateVendorPurchaseOrderItem', params) as Promise<UpdateVendorPurchaseOrderItemResult>;

export type UploadArtworkParams = { file: File; };
export type UploadArtworkResult = { upload: Upload; };
export const uploadArtwork = (params: UploadArtworkParams) => postToAPI('uploadArtwork', params) as Promise<UploadArtworkResult>;

export type UploadItemPhotosParams = { item: number; files: File[]; visible: boolean; };
export type UploadItemPhotosResult = { success: boolean; };
export const uploadItemPhotos = (params: UploadItemPhotosParams) => postToAPI('uploadItemPhotos', params) as Promise<UploadItemPhotosResult>;

export type UploadMenuImageParams = { file: File; };
export type UploadMenuImageResult = { image: string; };
export const uploadMenuImage = (params: UploadMenuImageParams) => postToAPI('uploadMenuImage', params) as Promise<UploadMenuImageResult>;

export type UseCreditsParams = { cart: string; applyCredits: boolean; };
export type UseCreditsResult = { cart: Cart; };
export const useCredits = (params: UseCreditsParams) => postToAPI('useCredits', params) as Promise<UseCreditsResult>;

export type ValidateAddressParams = Maybe<{ id?: Maybe<string>; name_prefix?: Maybe<string>; first_name?: Maybe<string>; middle_name?: Maybe<string>; last_name?: Maybe<string>; name_suffix?: Maybe<string>; company?: Maybe<string>; email?: Maybe<string>; phone?: Maybe<string>; address?: Maybe<string>; address_2?: Maybe<string>; city?: Maybe<string>; region?: Maybe<string>; state?: Maybe<string>; country?: Maybe<string>; postcode?: Maybe<string>; }> | undefined;
export type ValidateAddressResult = { address: AddressInfo; };
export const validateAddress = (params?: ValidateAddressParams) => postToAPI('validateAddress', params) as Promise<ValidateAddressResult>;

export type VerifyEmailParams = Maybe<{ cookie?: Maybe<string>; skip?: Maybe<boolean>; code?: Maybe<string>; acceptAccount?: Maybe<string>; }> | undefined;
export type VerifyEmailResult = AuthResponse;
export const verifyEmail = (params?: VerifyEmailParams) => postToAPI('verifyEmail', params) as Promise<VerifyEmailResult>;

export type WaitForPaymentCompletionParams = { payment: string; };
export type WaitForPaymentCompletionResult = { success: boolean; status?: Maybe<string>; };
export const waitForPaymentCompletion = (params: WaitForPaymentCompletionParams) => postToAPI('waitForPaymentCompletion', params) as Promise<WaitForPaymentCompletionResult>;

